import type {ModalProps, RenderTreeNodePayload} from '@mantine/core';
import {Modal, Tree} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {Textbox} from 'src/design/atoms/Textbox';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useAlert} from 'src/shared/hooks/useAlert';
import {AssetTreeLeaf} from 'src/app/assets/AssetTreeComponents';
import {useAssetTreeContent} from 'src/app/assets/AssetTreeContent';

export interface AssetTreeModalProps
  extends Omit<ModalProps, 'onClose' | 'title' | 'children'> {
  opened: boolean;
  onClose: () => void;
  onConfirm: (selectedNodeId: number | null) => void;
  title?: string;
  currentItemName?: string;
  currentItemType?: 'asset' | 'leaf';
  currentItemId?: number;
}

export const AssetTreeModal: React.FC<AssetTreeModalProps> = ({
  opened,
  onClose,
  onConfirm,
  title = 'Select New Location',
  currentItemName,
  currentItemId,
  ...modalProps
}) => {
  const {setAlert} = useAlert();
  const {
    searchTerm,
    handleSearchTerm,
    resetSearch,
    isLoading,
    filteredTreeNodeData,
    tree,
    refetch,
  } = useAssetTreeContent({
    treeQueryParams: {
      parentId: undefined,
      isLeaf: false,
      activeStates: [true],
    },
    skipQuery: !opened,
    excludeNodeId: currentItemId,
    expandOnMount: true,
  });

  React.useEffect(() => {
    if (opened) {
      void refetch();
      tree.clearSelected();
      tree.expandAllNodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, refetch]);

  const handleConfirm = () => {
    const selectedId = tree.selectedState[0];
    if (!selectedId) {
      setAlert('No asset selected.', 'error');
      return;
    }
    onConfirm(parseInt(selectedId));
    onClose();
  };

  const handleCancel = () => {
    resetSearch();
    tree.clearSelected();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={title}
      size="md"
      centered
      {...modalProps}>
      <Modal.Header pos={'sticky'} style={{padding: 0}}>
        <Box w="100%">
          <Text size="sm" c="dimmed" mb="sm">
            Select the new location for{' '}
            <Text component="span" fw={500}>
              {currentItemName}
            </Text>
            . The asset and all child assets will be moved to the selected
            asset.
          </Text>
          <Textbox
            label="Search"
            name="search"
            value={searchTerm}
            onChangeCallback={handleSearchTerm}
            autoComplete={false}
          />
          <Divider />
        </Box>
      </Modal.Header>

      {isLoading ? (
        <TableBodyRender isLoading={isLoading} />
      ) : (
        <Tree
          selectOnClick
          expandOnClick={false}
          data={filteredTreeNodeData}
          renderNode={(payload: RenderTreeNodePayload) => (
            <AssetTreeLeaf {...payload} />
          )}
          mb="md"
          tree={tree}
        />
      )}

      <Modal.Header pos={'sticky'} bottom={0} style={{padding: 0}}>
        <Group justify="end" w="100%">
          <DialogButton onClick={handleCancel}>Cancel</DialogButton>
          <DialogButton
            size="md"
            variant="filled"
            color="blue"
            onClick={handleConfirm}
            disabled={tree.selectedState.length === 0}>
            Move to asset
          </DialogButton>
        </Group>
      </Modal.Header>
    </Modal>
  );
};
